(() => {
  const config = window.AkeneoFilesIndexConfig || {};
  const locale = config.locale || document.documentElement.getAttribute('lang') || 'default';
  const sectionId = config.sectionId || 'akeneo-files-index-json';
  const cacheVersion = config.version || '';
  const rootUrl = typeof config.rootUrl === 'string' ? config.rootUrl : '';
  // Cache overlay per locale to avoid refetching all pages.
  const cacheKey = `akeneoFilesIndexOverlay:${locale}`;
  const eventName = 'akeneo-files-index:ready';

  const setReady = () => {
    window.AkeneoFilesIndexReady = true;
    document.dispatchEvent(new CustomEvent(eventName));
  };

  // Merge localized overlay into base AkeneoFilesIndex by SKU.
  const mergeOverlay = items => {
    if (!items || !window.AkeneoFilesIndex || !Array.isArray(window.AkeneoFilesIndex)) return;
    for (const entry of window.AkeneoFilesIndex) {
      const sku = entry && entry.sku ? String(entry.sku) : '';
      if (!sku) continue;
      const overlay = items[sku];
      if (!overlay) continue;
      if (overlay.title) entry.title = overlay.title;
      if (overlay.url) entry.url = overlay.url;
      if (overlay.productImage && overlay.productImage.url) {
        entry.productImage = overlay.productImage;
      }
    }
  };

  // Parse overlay JSON embedded in section HTML response.
  const parseOverlayFromHtml = html => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector('#AkeneoFilesIndexOverlay');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (e) {
      return null;
    }
  };

  // Use locale-aware rootUrl to avoid mixing languages.
  const buildOverlayUrl = page => {
    const base = rootUrl && rootUrl !== '/' ? rootUrl.replace(/\/$/, '') : '';
    return `${base}/?section_id=${encodeURIComponent(sectionId)}&page=${page}`;
  };

  const fetchOverlayPage = page => {
    const url = buildOverlayUrl(page);
    return fetch(url, { credentials: 'same-origin' })
      .then(res => (res.ok ? res.text() : null))
      .then(html => (html ? parseOverlayFromHtml(html) : null));
  };

  // Read and validate cached overlay for the current locale/version.
  const readCache = () => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || data.locale !== locale || data.version !== cacheVersion || !data.items) return null;
      return data;
    } catch (e) {
      return null;
    }
  };

  // Persist overlay for fast future loads.
  const writeCache = data => {
    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (e) {}
  };

  // Fetch all overlay pages with limited concurrency.
  const fetchAllPages = async (firstPageData) => {
    const total = Number(firstPageData.products_count || 0);
    const pageSize = Number(firstPageData.page_size || 250);
    const totalPages = pageSize > 0 ? Math.ceil(total / pageSize) : 1;
    const items = Object.assign({}, firstPageData.items || {});
    if (totalPages <= 1) return { items, products_count: total, page_size: pageSize };

    const pages = [];
    for (let i = 2; i <= totalPages; i++) pages.push(i);
    const concurrency = 3;
    let idx = 0;
    const workers = new Array(concurrency).fill(null).map(async () => {
      while (idx < pages.length) {
        const page = pages[idx++];
        const data = await fetchOverlayPage(page);
        if (data && data.items) {
          Object.assign(items, data.items);
        }
      }
    });
    await Promise.all(workers);
    return { items, products_count: total, page_size: pageSize };
  };

  // Initialize overlay merge and signal readiness.
  const init = async () => {
    if (!window.AkeneoFilesIndex || !Array.isArray(window.AkeneoFilesIndex)) {
      setReady();
      return;
    }
    window.AkeneoFilesIndexReady = false;
    try {
      const cached = readCache();
      if (cached && cached.items) {
        mergeOverlay(cached.items);
        return;
      }
      const firstPage = await fetchOverlayPage(1);
      if (!firstPage || !firstPage.items) return;
      const allData = await fetchAllPages(firstPage);
      mergeOverlay(allData.items);
      writeCache({
        locale,
        version: cacheVersion,
        items: allData.items,
        products_count: allData.products_count,
        page_size: allData.page_size,
        saved_at: Date.now(),
      });
    } catch (e) {
      console.warn('Akeneo overlay load failed; using base index', e);
    } finally {
      setReady();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
